import { HttpErrorResponse, HttpRequest } from '@angular/common/http';

/**
 * QA switch: simulate a server failure (HTTP 5xx → the `SRV` diagnostic toast)
 * without touching code or breaking the backend.
 *
 * ## How a tester uses it
 *
 * In the browser console of a staging / UAT / dev build whose runtime config has
 * `simulateFailureEnabled: true`:
 *
 *   __simulateSrv('CONVERSION_LISTING', 500)   // XBRL Files + Dashboard "recent" list now fail with a 500
 *   __simulateSrvScopes()                      // list the scope names
 *   __simulateSrvClear()                       // back to normal
 *
 * then reload or navigate to the page. The scenario lives in `localStorage` under
 * {@link SIMULATE_FAILURE_STORAGE_KEY} and expires after {@link SIMULATE_FAILURE_TTL_MS},
 * so a forgotten override cannot break tomorrow's session.
 *
 * ## Why it is safe
 *
 * Three independent gates keep it out of production — see
 * `simulate-failure.interceptor.ts` and `RuntimeConfig.simulateFailureEnabled`.
 * This file is pure (no Angular DI, no storage access) so the matching rules are
 * unit-testable on their own.
 *
 * ## Scopes are ENDPOINTS, not pages
 *
 * The XBRL Files listing and the Dashboard's recent-conversions list call the same
 * endpoint, so one scope breaks both. A scope is therefore named after the call.
 */

/** `localStorage` key. Outside the `mbrs-` prefix so logout does not clear it mid-test. */
export const SIMULATE_FAILURE_STORAGE_KEY = 'yyc-simulate-failure';

/** How long a stored scenario stays active. */
export const SIMULATE_FAILURE_TTL_MS = 60 * 60 * 1000;

/**
 * Scope name → URL fragment that identifies the endpoint. Add a line here to make
 * another call breakable; nothing else needs to change.
 */
export const SIMULATE_FAILURE_SCOPES = {
  CONVERSION_LISTING: '/mbrs/conversion-listing',
  CONVERSION_STATUS_COUNT: '/mbrs/conversion-status-count',
  XBRL_DATA_LISTING: '/mbrs/xbrl-data-listing',
  RETRIEVE_MBRS_DATA: '/mbrs/retrieve-mbrs-data',
  USER_PROFILE: '/user/profile',
  USER_LIST: '/user/users',
  IS_AUTHENTICATED: '/auth/is-authenticated',
} as const;

export type SimulateFailureScope = keyof typeof SIMULATE_FAILURE_SCOPES;

export interface SimulateFailureScenario {
  scope: SimulateFailureScope;
  /** A 5xx status. Anything else is refused: this switch exists for SRV only. */
  status: number;
  /** Epoch milliseconds after which the scenario is ignored. */
  expiresAt: number;
}

const MIN_SERVER_STATUS = 500;
const MAX_SERVER_STATUS = 599;

export function isSimulateFailureScope(value: unknown): value is SimulateFailureScope {
  return typeof value === 'string' && Object.prototype.hasOwnProperty.call(SIMULATE_FAILURE_SCOPES, value);
}

/** True for an integer status in the 5xx range. */
export function isServerErrorStatus(value: unknown): value is number {
  return Number.isInteger(value) && (value as number) >= MIN_SERVER_STATUS && (value as number) <= MAX_SERVER_STATUS;
}

/** Build the value the console helper stores. Throws on an unknown scope or non-5xx status. */
export function createSimulateFailureScenario(
  scope: unknown,
  status: unknown,
  now: number,
): SimulateFailureScenario {
  if (!isSimulateFailureScope(scope)) {
    throw new Error(`Unknown scope "${String(scope)}". Valid: ${Object.keys(SIMULATE_FAILURE_SCOPES).join(', ')}`);
  }
  if (!isServerErrorStatus(status)) {
    throw new Error(`Status must be a 5xx integer (got ${String(status)})`);
  }
  return { scope, status, expiresAt: now + SIMULATE_FAILURE_TTL_MS };
}

/**
 * Parse the stored value. Returns `null` for anything missing, malformed,
 * unrecognised or expired — a bad value must never break a request.
 */
export function parseSimulateFailureScenario(
  raw: string | null,
  now: number,
): SimulateFailureScenario | null {
  if (!raw) return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }
  if (parsed === null || typeof parsed !== 'object') return null;
  const { scope, status, expiresAt } = parsed as Record<string, unknown>;
  if (!isSimulateFailureScope(scope) || !isServerErrorStatus(status)) return null;
  if (typeof expiresAt !== 'number' || expiresAt <= now) return null;
  return { scope, status, expiresAt };
}

/** Does this request belong to the scenario's endpoint? */
export function matchesSimulateFailure(scenario: SimulateFailureScenario, request: HttpRequest<unknown>): boolean {
  return request.url.includes(SIMULATE_FAILURE_SCOPES[scenario.scope]);
}

/** A real `HttpErrorResponse`, shaped like the backend's 5xx, so every downstream branch behaves as it would for the real thing. */
export function buildSimulatedErrorResponse(
  request: HttpRequest<unknown>,
  scenario: SimulateFailureScenario,
): HttpErrorResponse {
  return new HttpErrorResponse({
    status: scenario.status,
    statusText: 'Simulated server failure (QA switch)',
    url: request.url,
    error: { status: false, statusCode: scenario.status, errors: ['Simulated server failure (QA switch)'], data: null },
  });
}
